/**
 * Le premier import d'une entreprise — la vérification de la liste initiale.
 *
 * Un DNS suivant se lit par différences : ce qui a changé depuis le dernier.
 * Le premier n'a rien à quoi se comparer : TOUT ce qu'il porte entre dans le
 * système, et c'est la seule fois où chaque valeur passe sous les yeux de la
 * chargée. D'où deux choses, partagées par le serveur et l'écran :
 *   - des alertes de vraisemblance, qui désignent les cellules à regarder ;
 *   - la lecture d'une correction tapée à l'écran, avec les MÊMES règles que
 *     la lecture du fichier : une date corrigée se lit comme une date lue.
 */
import { normaliserCompteur, normaliserDate, normaliserMatricule, normaliserSexe, } from './normalisation.js';
/** Les champs qu'une chargée peut corriger avant la création. */
export const CHAMPS_CORRIGEABLES = [
    'nom',
    'prenom',
    'matricule',
    'dateNaissance',
    'sexe',
    'fonction',
    'lieuAffectation',
    'dateEntree',
    'nbConjoint',
    'nbEnfant',
    /** Conjoint ou enfant : ne se corrige que sur la ligne d'un ayant droit. */
    'lien',
];
/**
 * Ce qui se corrige sur la ligne d'un conjoint ou d'un enfant, au premier
 * import : son identité et son lien. Rien de ce qui décrit un emploi.
 */
export const CHAMPS_CORRIGEABLES_AYANT_DROIT = [
    'nom',
    'prenom',
    'dateNaissance',
    'sexe',
    'lien',
];
/** Au-delà, c'est une autre colonne tombée là (un numéro de CIN, mesuré), pas un nombre d'enfants. */
const COMPTEUR_MAX = 20;
/** Une correction tapée à l'écran, lue comme le fichier l'aurait été. */
export function lireCorrection(champ, saisie) {
    const t = saisie.trim();
    switch (champ) {
        case 'nom':
            return t === ''
                ? { ok: false, message: 'Le nom ne peut pas être vide.' }
                : { ok: true, valeur: t };
        case 'prenom':
        case 'fonction':
        case 'lieuAffectation':
            return { ok: true, valeur: t === '' ? null : t };
        case 'matricule':
            return { ok: true, valeur: normaliserMatricule(t) };
        case 'dateNaissance':
        case 'dateEntree': {
            const d = normaliserDate(t, champ);
            const motif = d.anomalies[0]?.motif;
            if (motif === 'DATE_IMPOSSIBLE' || motif === 'JOUR_MOIS_INVERSES') {
                // Bien écrite, mais elle n'existe pas : le dire, plutôt que redemander le format.
                return { ok: false, message: 'Cette date n’existe pas (31 février, mois 13…).' };
            }
            if (motif === 'ANNEE_SUR_DEUX_CHIFFRES' || motif === 'ANNEE_COMPLETEE') {
                return { ok: false, message: 'Écrivez l’année en entier : 1974, pas 74.' };
            }
            return motif !== undefined
                ? { ok: false, message: 'Date illisible : écrivez-la JJ/MM/AAAA.' }
                : { ok: true, valeur: d.valeur };
        }
        case 'sexe': {
            const s = normaliserSexe(t);
            return s.anomalies.length > 0
                ? { ok: false, message: 'Écrivez M ou F.' }
                : { ok: true, valeur: s.valeur };
        }
        case 'nbConjoint':
        case 'nbEnfant': {
            if (t === '')
                return { ok: true, valeur: 0 };
            const n = normaliserCompteur(t, champ);
            return n.anomalies.length > 0 || n.valeur === null || n.valeur > COMPTEUR_MAX
                ? { ok: false, message: `Un nombre entre 0 et ${String(COMPTEUR_MAX)}.` }
                : { ok: true, valeur: n.valeur };
        }
        case 'lien': {
            const l = t
                .normalize('NFD')
                .replace(/[\u0300-\u036f]/g, '')
                .toUpperCase();
            if (/^(C|CONJ|CONJOINTE?|EPOUSE|EPOUX)$/.test(l))
                return { ok: true, valeur: 'CONJOINT' };
            if (/^(E|ENF|ENFANT|FILS|FILLE)$/.test(l))
                return { ok: true, valeur: 'ENFANT' };
            return { ok: false, message: 'Écrivez Conjoint ou Enfant.' };
        }
    }
}
const AGE_MIN = 16;
const AGE_MAX = 75;
function ajouterAnnees(d, annees) {
    return new Date(Date.UTC(d.getUTCFullYear() + annees, d.getUTCMonth(), d.getUTCDate()));
}
/**
 * Ce qui ne peut pas être vrai d'un salarié : un âge hors de 16-75 ans à la
 * date de la déclaration, une entrée avant ses 16 ans ou après la déclaration.
 */
export function alertesDeVraisemblance(d, reference) {
    const alertes = [];
    if (d.dateNaissance !== null) {
        const tropJeune = ajouterAnnees(d.dateNaissance, AGE_MIN) > reference;
        const tropVieux = ajouterAnnees(d.dateNaissance, AGE_MAX) < reference;
        if (tropJeune || tropVieux)
            alertes.push({ champ: 'dateNaissance', motif: 'AGE_IMPROBABLE' });
    }
    if (d.dateEntree !== null) {
        if (d.dateEntree > reference) {
            alertes.push({ champ: 'dateEntree', motif: 'ENTREE_FUTURE' });
        }
        else if (d.dateNaissance !== null && d.dateEntree < ajouterAnnees(d.dateNaissance, AGE_MIN)) {
            alertes.push({ champ: 'dateEntree', motif: 'ENTREE_AVANT_16_ANS' });
        }
    }
    return alertes;
}
/** Les champs dont l'absence se signale, quand la plupart des lignes les portent. */
export const CHAMPS_ATTENDUS = [
    'matricule',
    'dateNaissance',
    'sexe',
    'fonction',
    'dateEntree',
];
/** Part des lignes qui portent un champ pour que son absence ailleurs soit signalée. */
export const PART_CHAMP_ATTENDU = 0.5;
//# sourceMappingURL=verification.js.map